import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { captureCheckoutPromoCodeFromUrl, getCheckoutPromoCode } from '../utils/checkoutPromoCode';
import { retryCatalogRequest } from '../utils/catalogRetry';

const LIVE_SLUG = 'ai-bootcamp';
const SELF_PACED_SLUG = 'ai-bootcamp-self-paced';

/**
 * NO FALLBACK PRICES. EVER.
 *
 * Prices and promotions change (sales, early-bird windows, plan changes). A
 * hardcoded fallback would keep rendering a stale price after a change and
 * could show a buyer an amount that does not match what Stripe charges.
 *
 * If the catalog cannot be read, this hook reports an error and the page shows
 * that pricing is unavailable. It never invents a number.
 */

const centsToDisplay = (cents) => {
  if (!Number.isFinite(cents)) return null;
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
};

const promoTierLabel = (description, code) => {
  const hay = `${description || ''} ${code || ''}`.toLowerCase();
  if (hay.includes('super') && hay.includes('early')) return 'Super Early Bird';
  if (hay.includes('early') && hay.includes('bird')) return 'Early Bird';
  if (hay.includes('early')) return 'Early Bird';
  const beforeColon = String(description || '')
    .split(':')[0]
    ?.trim();
  return beforeColon || code || null;
};

const normalizePlan = (plan) => {
  const installments = plan.metadata?.installments || plan.installments || 1;
  const effective = plan.discountedAmount ?? plan.amount;
  const hasDiscount =
    plan.discountedAmount !== null &&
    plan.discountedAmount !== undefined &&
    plan.discountedAmount !== plan.amount;
  const totalAmount = effective * installments;
  const originalTotalAmount = plan.amount * installments;
  const savings = originalTotalAmount - totalAmount;

  return {
    planKey: plan.planKey,
    label: plan.label,
    installments,
    amount: effective,
    originalAmount: plan.amount,
    hasDiscount,
    totalAmount,
    originalTotalAmount,
    savingsAmount: savings > 0 ? savings : 0,
    display: centsToDisplay(effective),
    totalDisplay: centsToDisplay(totalAmount),
    originalDisplay: centsToDisplay(plan.amount),
    originalTotalDisplay: centsToDisplay(originalTotalAmount),
    savingsDisplay: savings > 0 ? centsToDisplay(savings) : null,
  };
};

/**
 * Single source of truth for the course sales surfaces: pricing, the active
 * tuition window, the upcoming cohort, and its sessions. Every displayed
 * number and date comes from the backend; nothing is hardcoded here.
 */
const useCourseCatalog = ({ courseId } = {}) => {
  const [plans, setPlans] = useState([]);
  const [selfPaced, setSelfPaced] = useState(null);
  const [productName, setProductName] = useState(null);
  const [activePromotion, setActivePromotion] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [tuitionSchedule, setTuitionSchedule] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [cohortName, setCohortName] = useState(null);
  const [catalogCohortName, setCatalogCohortName] = useState(null);
  const [catalogCohortStart, setCatalogCohortStart] = useState(null);
  const [checkoutAvailableUntil, setCheckoutAvailableUntil] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pricingError, setPricingError] = useState(null);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const retryCatalog = useCallback(() => {
    setIsLoading(true);
    setPricingError(null);
    setCatalogAttempt((attempt) => attempt + 1);
  }, []);

  const resolvedCourseId = courseId || process.env.REACT_APP_AI_BOT_BUILDER_BOOTCAMP_COURSE_ID;

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const { signal } = controller;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    setIsLoading(true);
    setSessions([]);
    setCohortName(null);

    // A buyer who arrived with a promo code in the link (for example an
    // email's ?promo=LUMIWEALTH66) sees that code's price and deadline, not
    // the Early Bird ladder at a different price (Rob, 2026-09-29). The
    // server prices the catalog with the code; if the code does not apply
    // the ordinary catalog is shown exactly as before.
    const readCatalog = (promoCode, options) => {
      const query = promoCode ? `?promoCode=${encodeURIComponent(promoCode)}` : '';
      return retryCatalogRequest(
        async () => {
          const res = await fetch(`${API_URL}/checkout/catalog${query}`, { signal });
          if (!res.ok) {
            const error = new Error(`catalog ${res.status}`);
            error.status = res.status;
            throw error;
          }
          return res.json();
        },
        { signal, surface: 'course_catalog', ...options },
      );
    };

    const loadCatalog = async () => {
      try {
        const search = window.location.search;
        const linkPromoCode =
          captureCheckoutPromoCodeFromUrl(search) || getCheckoutPromoCode(search);
        let data = null;
        let codeApplied = false;
        if (linkPromoCode) {
          try {
            // A failed promo lookup is recoverable through the ordinary catalog.
            const codeData = await readCatalog(linkPromoCode, { reportFailure: false });
            if (codeData?.promotionCodeValidation?.status === 'applied') {
              data = codeData;
              codeApplied = true;
            }
          } catch (codeError) {
            if (signal.aborted || codeError?.name === 'AbortError') throw codeError;
            console.warn('[useCourseCatalog] Promo code pricing unavailable', codeError);
          }
        }
        if (!data) data = await readCatalog(null, { promoCodePresent: Boolean(linkPromoCode) });

        const live = data.products?.find((p) => p.slug === LIVE_SLUG);
        const sp = data.products?.find((p) => p.slug === SELF_PACED_SLUG);
        if (!mounted) return;

        // Show every purchasable plan the catalog returns, including the
        // five-payment option. Buyers choose; we do not hide options.
        const visible = (live?.plans || [])
          .filter((p) => p.stripePriceId)
          .map(normalizePlan)
          .sort((a, b) => a.installments - b.installments);

        if (!visible.length) {
          throw new Error('catalog returned no purchasable plans');
        }

        setPlans(visible);
        setSelfPaced(
          sp?.plans?.length
            ? {
                ...normalizePlan(sp.plans[0]),
                productName: sp.name || null,
                includedProDays: sp.metadata?.includedProDays || null,
              }
            : null,
        );
        setProductName(live?.name || null);
        setCatalogCohortName(live?.metadata?.cohort?.name || null);
        setCatalogCohortStart(live?.metadata?.cohort?.startsAt || null);
        setCheckoutAvailableUntil(live?.metadata?.checkoutAvailableUntil || null);
        setActivePromotion(data.activePromotion || null);
        setAppliedPromoCode(codeApplied ? linkPromoCode : null);
        setTuitionSchedule(
          !codeApplied && data.tuitionSchedule?.windows?.length ? data.tuitionSchedule : null,
        );
        setPricingError(null);
      } catch (err) {
        if (!mounted || signal.aborted || err?.name === 'AbortError') return;
        // Never invent a price. Report the failure and let the page say so.
        console.error('[useCourseCatalog] Could not load pricing from the catalog', err);
        setPlans([]);
        setSelfPaced(null);
        setProductName(null);
        setCatalogCohortName(null);
        setCatalogCohortStart(null);
        setCheckoutAvailableUntil(null);
        setActivePromotion(null);
        setAppliedPromoCode(null);
        setTuitionSchedule(null);
        setPricingError(err instanceof Error ? err : new Error('pricing_unavailable'));
      }
    };

    const loadSessions = async () => {
      if (!resolvedCourseId) return;
      try {
        const res = await fetch(`${API_URL}/courses/${resolvedCourseId}/bootcamp-data`);
        if (!res.ok) throw new Error(`sessions ${res.status}`);
        const data = await res.json();
        if (!mounted) return;

        // bootcamp-data marks the soonest upcoming cohort as `current` and later
        // cohorts as `future`. Prefer current so a stale duplicate cohort in
        // `future` cannot hide the live syllabus.
        const group = data.sessionGroups?.current || data.sessionGroups?.future?.[0] || null;
        if (group) {
          setCohortName(group.name || null);
          setSessions(
            (group.sessions || [])
              .filter((s) => s.scheduledAt)
              .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)),
          );
        }
      } catch (err) {
        /* evergreen mode: page renders without dates */
      }
    };

    Promise.all([loadCatalog(), loadSessions()]).finally(() => {
      if (mounted) setIsLoading(false);
    });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [resolvedCourseId, catalogAttempt]);

  const upfrontPlan = useMemo(
    () => plans.find((p) => p.installments === 1) || plans[0] || null,
    [plans],
  );
  const installmentPlan = useMemo(() => plans.find((p) => p.installments > 1) || null, [plans]);

  // Session rows are most specific. Catalog cohort metadata keeps enrollment
  // truthful before the detailed weekly schedule is published.
  const sessionStart = sessions.length ? new Date(sessions[0].scheduledAt) : null;
  const metadataStart = useMemo(() => {
    if (!catalogCohortStart) return null;
    const value = new Date(catalogCohortStart);
    return Number.isNaN(value.getTime()) ? null : value;
  }, [catalogCohortStart]);
  const checkoutCloses = useMemo(() => {
    if (!checkoutAvailableUntil) return null;
    const value = new Date(checkoutAvailableUntil);
    return Number.isNaN(value.getTime()) ? null : value;
  }, [checkoutAvailableUntil]);
  const cohortStart = sessionStart || metadataStart;
  const isEnrollable = Boolean(
    cohortStart &&
    cohortStart.getTime() > Date.now() &&
    (!checkoutCloses || checkoutCloses.getTime() > Date.now()),
  );

  // A promotion only counts as live when the backend gave us a future end date.
  const promoEndsAt = useMemo(() => {
    const raw = activePromotion?.endDate || activePromotion?.end_date;
    if (!raw) return null;
    const d = new Date(raw);
    return !Number.isNaN(d.getTime()) && d.getTime() > Date.now() ? d : null;
  }, [activePromotion]);

  const promoName = activePromotion?.description || activePromotion?.code || null;
  const promoTierName = useMemo(
    () => promoTierLabel(activePromotion?.description, activePromotion?.code),
    [activePromotion],
  );
  const hasActivePromo = Boolean(promoEndsAt && plans.some((p) => p.hasDiscount));
  const enrollmentDeadline = promoEndsAt;
  const currentTuitionWindow = useMemo(
    () => tuitionSchedule?.windows?.find((window) => window.isCurrent) || null,
    [tuitionSchedule],
  );

  const formatSessionDate = useCallback(
    (value) =>
      new Date(value).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/New_York',
      }),
    [],
  );

  // Prefer the live session-group name; fall back to catalog tuition cohort.
  const resolvedCohortName = cohortName || tuitionSchedule?.cohortName || catalogCohortName || null;

  return {
    isLoading,
    pricingError,
    retryCatalog,
    plans,
    upfrontPlan,
    installmentPlan,
    selfPaced,
    productName,
    activePromotion,
    appliedPromoCode,
    tuitionSchedule,
    currentTuitionWindow,
    promoEndsAt,
    promoName,
    promoTierName,
    hasActivePromo,
    enrollmentDeadline,
    sessions,
    cohortName: resolvedCohortName,
    cohortStart,
    isEnrollable,
    formatSessionDate,
  };
};

export default useCourseCatalog;
